// ════════════════════════════════════════════════════════════════════════════
//  ManDana — merge
//  ---------------------------------------------------------------------------
//  Reconciling one day held by two devices. Pure functions, no storage, no
//  network — which is why every rule below is a test rather than a hope.
//
//  THE BUG THIS CLOSES. Conflicts used to resolve last-write-wins on a WHOLE
//  DAY: server/sync.js compares client_seen_at per key, and the key is
//  `events:2026-07-30`. Edit record A on the phone and record B on the desktop
//  on the same date, and whichever pushed second replaced the entire day. The
//  other edit was gone — silently, with nothing reported anywhere. For anyone
//  actually running two devices, which is the whole point of sync, that is the
//  most likely real loss in the product.
//
//  WHY THE CLIENT MERGES. server/sync.js says the server never inspects a
//  value, and that is not incidental — it is what keeps end-to-end encryption
//  reachable. A server that has to merge can never be handed ciphertext, so the
//  moment merging moves server-side the sovereignty promise in the product
//  vision quietly becomes impossible. The server stays a dumb journal; the
//  client reconciles on pull.
//
//  WHY NOT CRDTs. A CRDT would give this for free and cost a dependency, an
//  opaque binary state, and a much larger idea to hold. Records have stable
//  uids and edits are whole-record, so a keyed merge with per-record timestamps
//  and tombstones covers the real cases at a fraction of the size. The vision
//  document's own line is that CRDTs enter only where concurrent editing truly
//  needs them — concurrent editing of the same TEXT is where that starts, and
//  this is not that yet.
//
//  CONVERGENCE IS THE PROPERTY THAT MATTERS. Two devices merging the same pair
//  of versions must reach the same answer regardless of which one they call
//  "local". Anything asymmetric — including an innocent-looking tie-break that
//  prefers one argument — makes them overwrite each other forever.
// ════════════════════════════════════════════════════════════════════════════
/* First module in TypeScript. Chosen as the pilot deliberately: it is pure
   functions with no DOM and no storage, it has the densest test coverage in the
   repo, and its failure mode — losing an edit — is the one where a wrong shape
   slipping through costs the most. The .jsx files around it are untouched and
   stay that way; see tsconfig.json for why that is the point. */

/** A record as it lives inside a day document. Everything past `uid` is the
    UI's business — the merge only needs identity, a time, and a stamp. */
interface MdRecord {
  uid: string;
  min?: number;
  updatedAt?: number;
  [field: string]: unknown;
}

/** uid -> epoch ms at which it was deleted. */
type MdTombstones = Record<string, number>;

/** uid -> true. Completions carry no per-entry time; see mdMergeCompleted. */
type MdCompleted = Record<string, true>;

/** One element of a drawing, as the engine keeps it. Everything past `id` is
    Excalidraw's business — the merge needs identity, a version, and the flag
    that says the element is a tombstone rather than a shape. */
interface MdSceneElement {
  id: string;
  version?: number;
  versionNonce?: number;
  isDeleted?: boolean;
  index?: string;
  [field: string]: unknown;
}

interface MdScene {
  elements: MdSceneElement[];
  appState: Record<string, unknown>;
}

(function (g: any) {
  'use strict';

  const arr = (v: unknown): MdRecord[] => (Array.isArray(v) ? (v as MdRecord[]) : []);
  const obj = <T extends object>(v: unknown): T =>
    (v && typeof v === 'object' && !Array.isArray(v) ? (v as T) : ({} as T));

  /* A record with no updatedAt predates stamping. Treating it as 0 means a
     stamped version always wins, which is right: the stamped one was written by
     a client that knew about merging. */
  const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  const stampOf = (r: MdRecord | undefined): number => (r && num(r.updatedAt) ? r.updatedAt : 0);

  /* Content identity, ignoring the stamp itself — otherwise every record would
     compare as changed the moment it was stamped. Keys are sorted so two
     objects built in a different order still compare equal. */
  /* Widened past MdRecord because a drawing's elements need the same symmetric
     tie-break and are identified by `id` rather than `uid`. Nothing here reads
     either field except the fallback, which takes whichever is present. */
  function contentKey(r: Record<string, unknown>): string {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(r).sort()) {
      if (k === 'updatedAt') continue;
      out[k] = r[k];
    }
    try { return JSON.stringify(out); } catch (e) { return String((r && (r.uid ?? r.id)) ?? ''); }
  }

  function usable(r: unknown): r is MdRecord {
    if (!r || typeof r !== 'object' || Array.isArray(r)) return false;
    const uid = (r as { uid?: unknown }).uid;
    return typeof uid === 'string' && uid.length > 0;
  }

  /* Pick between two versions of the same record.

     Later stamp wins. On an exact tie the answer must not depend on which side
     was passed first, so it falls back to comparing the serialised content and
     taking the greater one. Arbitrary, but symmetric — and symmetric is the
     only property that matters here. */
  function pick(a: MdRecord, b: MdRecord): MdRecord {
    const sa = stampOf(a), sb = stampOf(b);
    if (sa !== sb) return sa > sb ? a : b;
    const ca = contentKey(a), cb = contentKey(b);
    if (ca === cb) return a;
    return ca > cb ? a : b;
  }

  /** Merge two versions of one day's records, honouring deletions. */
  function mdMergeRecords(local: unknown, remote: unknown, tombstones: unknown): MdRecord[] {
    const tombs = obj<MdTombstones>(tombstones);
    const byUid = new Map<string, MdRecord>();

    for (const r of arr(local).concat(arr(remote))) {
      if (!usable(r)) continue;                 // no identity: nothing to merge into
      const seen = byUid.get(r.uid);
      byUid.set(r.uid, seen ? pick(seen, r) : r);
    }

    const out: MdRecord[] = [];
    for (const [uid, r] of byUid) {
      /* A tombstone only wins against a version older than the deletion. A
         record re-created afterwards must come back, or deleting something and
         writing it again would be permanently impossible. */
      const t = tombs[uid];
      if (num(t) && stampOf(r) <= t) continue;
      out.push(r);
    }

    /* Deterministic order. The UI re-sorts by time anyway, but two devices
         holding the same set in a different order would keep pushing each other
         a "changed" document forever. */
    out.sort((a, b) => {
      const ma = num(a.min) ? a.min : -1;
      const mb = num(b.min) ? b.min : -1;
      return ma !== mb ? ma - mb : (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0);
    });
    return out;
  }

  /** Union of two tombstone maps, keeping the later deletion for each uid. */
  function mdMergeTombstones(local: unknown, remote: unknown): MdTombstones {
    const out: MdTombstones = {};
    for (const src of [obj<MdTombstones>(local), obj<MdTombstones>(remote)]) {
      for (const [uid, at] of Object.entries(src)) {
        if (!uid || !num(at)) continue;               // junk from an older client
        const seen = out[uid];
        if (!num(seen) || at > seen) out[uid] = at;
      }
    }
    return out;
  }

  /* Stamp what changed, and note what vanished.

     Called on every write, so it is what gives a merge anything to compare.
     Crucially it leaves an UNTOUCHED record alone: if a save re-stamped
     everything, an idle device that merely opened the app would win every
     conflict against a device that had actually been edited. */
  function mdStampRecords(prev: unknown, next: unknown, now?: number): { records: MdRecord[]; tombstones: MdTombstones } {
    const at = num(now) ? now : Date.now();
    const before = new Map<string, MdRecord>();
    for (const r of arr(prev)) if (usable(r)) before.set(r.uid, r);

    const records: MdRecord[] = [];
    const kept = new Set<string>();
    for (const r of arr(next)) {
      if (!usable(r)) continue;
      kept.add(r.uid);
      const old = before.get(r.uid);
      if (old && contentKey(old) === contentKey(r)) {
        // unchanged: carry the existing stamp forward untouched
        records.push(num(r.updatedAt) ? r : { ...r, updatedAt: stampOf(old) });
      } else {
        /* An edit must ALWAYS outrank the version it was made from, and
           Date.now() cannot guarantee that: two writes in the same millisecond
           get the same stamp, the merge falls through to its content tie-break,
           and a real edit can lose to the stale version it replaced. Found by
           driving the actual app rather than by the unit tests, which had been
           handing in conveniently distinct timestamps.

           Forcing strict monotonicity per record costs nothing and removes the
           whole class: a changed record is never allowed to tie with its own
           predecessor. */
        records.push({ ...r, updatedAt: Math.max(at, stampOf(old) + 1) });
      }
    }

    const tombstones: MdTombstones = {};
    for (const uid of before.keys()) if (!kept.has(uid)) tombstones[uid] = at;

    return { records, tombstones };
  }

  /* Completions merge as a union.

     They are a map of uid -> true with no per-entry timestamp, so there is
     nothing to compare. The tradeoff is deliberate and worth stating: un-marking
     a prayer on one device can be undone by the other. Against that, letting a
     stale map clear the other device's would erase a record of worship. Between
     an annoyance and a loss, this takes the annoyance. */
  function mdMergeCompleted(local: unknown, remote: unknown): MdCompleted {
    const out: MdCompleted = {};
    for (const src of [obj<MdCompleted>(local), obj<MdCompleted>(remote)]) {
      for (const [uid, v] of Object.entries(src)) if (uid && v) out[uid] = true;
    }
    return out;
  }

  g.mdMergeRecords = mdMergeRecords;
  g.mdMergeTombstones = mdMergeTombstones;
  g.mdStampRecords = mdStampRecords;
  g.mdMergeCompleted = mdMergeCompleted;
  g.mdMergeScene = mdMergeScene;

  // CommonJS escape hatch, following md-overlap.js's lead, so a node test
  // runner can require this without a DOM. Declared locally rather than pulling
  // in @types/node for one symbol.
  /* ── one board, two devices ───────────────────────────────────────────────
     The same shape as mdMergeRecords, against the engine's own bookkeeping
     rather than ours. Excalidraw stamps every element it touches with a
     `version` it increments, a random `versionNonce`, and — this is the part
     that makes a real merge possible — it does not remove a deleted element,
     it sets `isDeleted` on it. So identity, stamp and tombstone are all
     already there, maintained by the thing that draws.

     WHY THE TOMBSTONE TRAVELS. A deleted element is kept in the output rather
     than dropped. Dropping it would let a device that still holds the stroke
     push it straight back on the next sync, and the deletion would never
     stick — the same reasoning that keeps day tombstones as their own
     document.

     ORDER. Elements are sorted by the engine's fractional `index`, then by id.
     Deterministic order is not cosmetic: two devices holding the same set in a
     different order would each read the other's copy as changed and push it
     back for ever. Where `index` is missing — a scene written by a build older
     than fractional indices — this can restack elements that overlap. That is
     a visible cost, accepted knowingly, because the alternative is a pair of
     devices that never settle. */
  const elArr = (v: unknown): MdSceneElement[] =>
    (Array.isArray(v) ? (v as MdSceneElement[]) : []);
  const versionOf = (e: MdSceneElement): number => (num(e.version) ? e.version : 0);

  function usableEl(e: unknown): e is MdSceneElement {
    if (!e || typeof e !== 'object' || Array.isArray(e)) return false;
    const id = (e as { id?: unknown }).id;
    return typeof id === 'string' && id.length > 0;
  }

  /* Later version wins. On a tie the answer must not depend on which side was
     passed first: versionNonce is the engine's own tie-break and is random per
     edit, so it separates two genuinely concurrent versions; if that ties too,
     the serialised content does, exactly as `pick` does for records. */
  function pickEl(a: MdSceneElement, b: MdSceneElement): MdSceneElement {
    const va = versionOf(a), vb = versionOf(b);
    if (va !== vb) return va > vb ? a : b;
    const na = num(a.versionNonce) ? a.versionNonce : 0;
    const nb = num(b.versionNonce) ? b.versionNonce : 0;
    if (na !== nb) return na > nb ? a : b;
    const ca = contentKey(a), cb = contentKey(b);
    if (ca === cb) return a;
    return ca > cb ? a : b;
  }

  /** Merge two versions of one drawing. */
  function mdMergeScene(local: unknown, remote: unknown): MdScene {
    const byId = new Map<string, MdSceneElement>();
    for (const e of elArr((local as MdScene | null)?.elements)
      .concat(elArr((remote as MdScene | null)?.elements))) {
      if (!usableEl(e)) continue;               // no identity: nothing to merge into
      const seen = byId.get(e.id);
      byId.set(e.id, seen ? pickEl(seen, e) : e);
    }

    const elements = [...byId.values()].sort((a, b) => {
      const ia = typeof a.index === 'string' ? a.index : '';
      const ib = typeof b.index === 'string' ? b.index : '';
      if (ia !== ib) return ia < ib ? -1 : 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

    /* appState is the pen you were last holding, not the drawing — but it still
       has to converge, or the two devices would trade the document back and
       forth over a stroke width. Same symmetric tie-break as everything else. */
    const la = obj<Record<string, unknown>>((local as MdScene | null)?.appState);
    const ra = obj<Record<string, unknown>>((remote as MdScene | null)?.appState);
    const lk = contentKey(la), rk = contentKey(ra);
    const appState = lk === rk || lk > rk ? la : ra;

    return { elements, appState };
  }

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdMergeRecords, mdMergeTombstones, mdStampRecords, mdMergeCompleted,
      mdMergeScene };
  }
})(typeof window !== 'undefined' ? window : globalThis);
